/**
 * FS-838 — the system tonnage in words: which unit gave it, on what evidence,
 * and what disagrees with it.
 *
 * The resolver (lambdas `shared/capacity.go`, FS-835) already chose the
 * winner and projected it with its component, side, source and capacity
 * source, plus the best disagreeing value. This module only says those
 * fields out loud. It never compares two tonnages and never picks one; every
 * judgement is the projection's.
 *
 * Every screen that says something about the system tonnage calls these —
 * the Overview, the system airflow tile, the Device List cell, its marker
 * and its CSV — so one value is never worded two ways. Each function takes
 * whatever the API sent, absent fields included, and never throws.
 */

import { sourceLabel } from './equipmentQuality';
import { own } from './ownKey';

type Maybe = string | null | undefined;
type MaybeNumber = number | string | null | undefined;

/**
 * `app_hvac_tonnage_component`, the fixture vocabulary. The words sort in
 * the same order as the codes, which is what lets the Device List's
 * Tonnage From column sort on the code and still read alphabetically.
 */
const COMPONENT_NAMES: Record<string, string> = {
  air_handler: 'air handler',
  ductless: 'ductless head',
  furnace: 'furnace',
  indoor_coil: 'indoor coil',
  indoor_unit: 'indoor unit',
  outdoor_unit: 'outdoor unit',
  package_unit: 'packaged unit',
};

/** The part a component code names, lower case for the middle of a sentence. */
export function componentName(component: Maybe): string {
  return own(COMPONENT_NAMES, component) ?? 'unrecorded part';
}

/**
 * The `equipmentSide` vocabulary. The disagreeing value carries only a side,
 * so it is named by that; a side cannot tell a furnace from an air handler.
 */
const SIDE_NAMES: Record<string, string> = {
  outdoor_condenser: 'outdoor unit',
  indoor_air_handler: 'indoor unit',
  indoor_coil: 'indoor coil',
  package_unit: 'packaged unit',
};

export function sideName(side: Maybe): string {
  return own(SIDE_NAMES, side) ?? 'another record';
}

/** A number as the server sent it (`3`, `2.5`), never rounded; a dash for none. */
const numberText = (value: MaybeNumber): string => {
  if (value === null || value === undefined || value === '') return '—';
  return String(value);
};

const lowerFirst = (s: string): string => (s ? s.charAt(0).toLowerCase() + s.slice(1) : s);

/** Rows 2–12 of the answer table, for a part already named. */
const evidenceSentence = (c: string, source: Maybe, capacitySource: Maybe): string => {
  if (source === 'user') return `Entered by the homeowner on the ${c}`;
  if (source === 'admin') return `Entered by an operator on the ${c}`;
  // A capacity worked back from a cooling BTU rating is weak whoever's
  // reading it came off, unless a person entered it (F28, DDA §5.1).
  if (capacitySource === 'derived' || source === 'derived') {
    return `Worked back from the ${c}'s cooling BTU rating; not used for CFM per ton`;
  }
  if (source === 'spec_lookup') return `From the ${c}'s manufacturer document`;
  if (source === 'ai_photo') return `From a label photo of the ${c}`;
  if (source === 'ai_model_decode') return `Decoded from the ${c}'s model number`;
  if (source === 'inferred') return `Inferred for the ${c}`;
  if (!source && capacitySource === 'nameplate') {
    return `From the ${c}'s nameplate (origin not recorded)`;
  }
  if (!source && capacitySource === 'model_decode') {
    return `Decoded from the ${c}'s model number (origin not recorded)`;
  }
  return `On file for the ${c}, source unknown`;
};

export interface TonnageAnswerFacts {
  component: Maybe;
  source: Maybe;
  capacitySource: Maybe;
  value: MaybeNumber;
}

/**
 * Which unit answered and on what evidence, as one sentence. The order is the
 * ladder's own evidence order (DDA `tonnageEvidence`): the "up to" last
 * resort first, then a person, then a figure worked back from BTU, then the
 * document, the label and the model number.
 */
export function tonnageAnswerLabel(f: TonnageAnswerFacts): string {
  const c = componentName(f.component);
  if (f.capacitySource === 'blower_maximum') {
    const where = f.source === 'spec_lookup' ? 'manufacturer document' : 'model number';
    return `Upper bound: the ${c}'s ${where} gives "up to ${numberText(f.value)} t"; `
      + 'used only because nothing else gives a size';
  }
  return evidenceSentence(c, f.source, f.capacitySource);
}

/** The fields of the server's `SystemTonnage` the answer line reads. */
export interface SystemTonnageFacts {
  value?: MaybeNumber;
  component?: Maybe;
  side?: Maybe;
  source?: Maybe;
  quality?: Maybe;
  reason?: Maybe;
  eligible?: boolean | null;
  conflict?: boolean | null;
  upper_bound?: boolean | null;
  /** The winner's own capacity source, when the API carries it on the snapshot. */
  capacity_source?: Maybe;
  /** The winner's own document id, when the API carries it on the snapshot. */
  document_id?: number | string | null;
  alt?: {
    value?: MaybeNumber;
    side?: Maybe;
    source?: Maybe;
    document_id?: number | string | null;
  } | null;
}

const hasOwn = (o: object, key: string): boolean => Object.prototype.hasOwnProperty.call(o, key);

const PERSON_SOURCES = new Set(['user', 'admin']);

/** The projection is out of date, so the server's quality and reason no longer describe the value. */
const isStale = (st: SystemTonnageFacts): boolean =>
  st.reason === 'projection_stale' || st.quality === 'tonnage_unavailable';

/**
 * FS-838 (review F1) — the capacity source behind a `SystemTonnage`. Taken,
 * in order, from:
 *
 * 1. the snapshot itself, when the API carries `capacity_source` on it;
 * 2. `sameSnapshot` — the caller's `hvac_tonnage_capacity_source` from the
 *    SAME response the winner came from (pass `undefined` when there is
 *    none; `null` is a recorded "none");
 * 3. the server's own grading of this snapshot, which it computed from the
 *    capacity source and which is exact for the values the answer table
 *    tells apart: `upper_bound` is `blower_maximum`; a `tonnage_from_btu`
 *    refusal or the `tonnage_derived_btu` rung is `derived`; an
 *    `_unattributed` rung is a nameplate or a model decode.
 *
 * A stale projection replaces the rung and the reason, so nothing past the
 * upper-bound flag is readable from it; the result is then `undefined`,
 * meaning "not recorded on this read". Nothing is guessed.
 */
export function systemTonnageCapacitySource(
  st: SystemTonnageFacts | null | undefined,
  sameSnapshot?: Maybe,
): string | null | undefined {
  if (!st) return undefined;
  if (hasOwn(st, 'capacity_source')) return st.capacity_source ?? null;
  if (sameSnapshot !== undefined) return sameSnapshot;
  if (st.upper_bound === true) return 'blower_maximum';
  if (isStale(st)) return undefined;
  if (st.reason === 'tonnage_from_btu' || st.quality === 'tonnage_derived_btu') return 'derived';
  if (!st.quality) return undefined;
  if (st.quality.endsWith('_nameplate_unattributed')) return 'nameplate';
  if (st.quality.endsWith('_decode_unattributed')) return 'model_decode';
  // Without a source the rung no longer separates "no capacity method"
  // from a method on an unrecognised component, so it is not read as either.
  if (!st.source) return undefined;
  return null;
}

/**
 * The answer line for a server `SystemTonnage`, as the Overview and the
 * airflow tile print it. When the capacity source cannot be known for this
 * read and the sentence would depend on it, it says the source is not
 * recorded rather than naming evidence nobody has.
 */
export function systemTonnageAnswer(st: SystemTonnageFacts, sameSnapshot?: Maybe): string {
  const capacitySource = systemTonnageCapacitySource(st, sameSnapshot);
  const independent = (st.source != null && PERSON_SOURCES.has(st.source)) || st.source === 'derived';
  if (capacitySource === undefined && !independent) {
    return `On file for the ${componentName(st.component)}, source not recorded`;
  }
  return tonnageAnswerLabel({
    component: st.component,
    source: st.source,
    capacitySource: capacitySource ?? null,
    value: st.value,
  });
}

const sameScalar = (a: unknown, b: unknown): boolean =>
  (a === undefined || a === null || a === '' ? null : String(a))
  === (b === undefined || b === null || b === '' ? null : String(b));

/**
 * FS-838 (review F2) — whether two `SystemTonnage` objects describe the same
 * projection snapshot: the same winner, how it was graded, and the same
 * disagreement. The Overview takes the winner from the airflow response and
 * the documents from the device detail; evidence is shown only when both
 * reads say the same thing, so a document never sits under a winner or a
 * disagreement it does not belong to. The same object is trivially the same
 * snapshot; two different reads match only when both carry
 * `capacity_source` and `document_id` and those agree too.
 */
export function sameTonnageSnapshot(
  a: SystemTonnageFacts | null | undefined,
  b: SystemTonnageFacts | null | undefined,
): boolean {
  if (!a || !b) return false;
  if (a === b) return true;
  const scalars: Array<keyof SystemTonnageFacts> = [
    'value', 'component', 'side', 'source', 'quality', 'reason', 'eligible', 'conflict', 'upper_bound',
  ];
  if (!scalars.every((k) => sameScalar(a[k], b[k]))) return false;
  // Review F7: the scalars above can agree while the evidence behind them
  // changed (a derived capacity became a nameplate one; document 18 became
  // 19 at the same size). Only the snapshot's own capacity source and
  // document id prove the provenance, so a read without them (an older API)
  // is never taken to match another read.
  for (const k of ['capacity_source', 'document_id'] as const) {
    if (!hasOwn(a, k) || !hasOwn(b, k) || !sameScalar(a[k], b[k])) return false;
  }
  const altA = a.alt ?? null;
  const altB = b.alt ?? null;
  if (!altA || !altB) return !altA && !altB;
  return (['value', 'side', 'source', 'document_id'] as const).every((k) => sameScalar(altA[k], altB[k]));
}

/** `3 t`, or `up to 4 t` for a blower unit's maximum; a dash for no value. */
export function tonnageValueText(value: MaybeNumber, upperBound?: boolean | null): string {
  if (value === null || value === undefined || value === '') return '—';
  return upperBound ? `up to ${value} t` : `${value} t`;
}

/**
 * FS-951 — the records as the equipment drawer's tabs name them
 * (`photoReviewWords` TAB_WORDS; a test holds the two together).
 */
export const RECORD_NAMES: Record<string, string> = {
  hvac: 'Indoor unit',
  ac: 'Outdoor unit',
  coil: 'Indoor coil (A-coil)',
};

/** FS-951 — the model whose manufacturer document states the disagreeing value, and the records carrying it. */
export interface TonnageAltModel {
  model?: string | null;
  records?: string[] | null;
}

export interface TonnageConflictFacts {
  alt: MaybeNumber;
  altSide: Maybe;
  altSource: Maybe;
  /** FS-951: only the device detail has it; without it the side names the part. */
  altModel?: TonnageAltModel | null;
}

/** "the Indoor unit record", "the Indoor unit and Outdoor unit records"; null for none known. */
function recordsText(records: string[] | null | undefined): string | null {
  const names = (records ?? []).map((r) => own(RECORD_NAMES, r)).filter((n): n is string => !!n);
  if (names.length === 0) return null;
  return names.length === 1 ? `the ${names[0]} record` : `the ${names.slice(0, -1).join(', ')} and ${names[names.length - 1]} records`;
}

/** The credible value that disagrees with the winner, and the record it is on. */
export function tonnageConflictLabel(f: TonnageConflictFacts): string {
  const p = sideName(f.altSide);
  const alt = numberText(f.alt);
  switch (f.altSource) {
    case 'spec_lookup': {
      // FS-951: the side says which part the document is about, not which
      // record holds its model; device 229's outdoor unit model sat on the
      // Indoor unit record, so a reviewer opening the Outdoor unit found none.
      const model = f.altModel?.model?.trim();
      if (model) {
        const where = recordsText(f.altModel?.records);
        return where
          ? `Disagreement: the manufacturer document for model ${model} (on ${where}) says ${alt} t.`
          : `Disagreement: the manufacturer document for model ${model} says ${alt} t.`;
      }
      return `Disagreement: the ${p}'s manufacturer document says ${alt} t.`;
    }
    case 'ai_photo':
      return `Disagreement: the ${p}'s label says ${alt} t.`;
    case 'user':
      return `Disagreement: the homeowner entered ${alt} t on the ${p}.`;
    case 'admin':
      return `Disagreement: an operator entered ${alt} t on the ${p}.`;
    case null:
    case undefined:
    case '':
      return `Disagreement: the ${p}'s nameplate says ${alt} t (origin not recorded).`;
    default:
      // The resolver only projects person, document and label disagreements
      // today; anything else is still named rather than dropped.
      return `Disagreement: the ${p}'s record says ${alt} t (${sourceLabel(f.altSource)}).`;
  }
}

/** A ductless head the resolver would not size from a larger outdoor unit. */
export function ductlessUnresolvedLabel(alt: MaybeNumber): string {
  const unit = alt === null || alt === undefined || alt === ''
    ? 'a larger outdoor unit'
    : `a larger outdoor unit (${alt} t)`;
  return `No system size: a ductless head sits on ${unit}, which likely serves more than one head.`;
}

/** The Device List row fields the marker reads. */
export interface TonnageMarkerRow {
  app_hvac_tonnage?: MaybeNumber;
  app_hvac_tonnage_source?: Maybe;
  app_hvac_tonnage_capacity_source?: Maybe;
  app_hvac_tonnage_component?: Maybe;
  app_hvac_tonnage_conflict?: boolean | null;
  app_hvac_tonnage_alt?: MaybeNumber;
  app_hvac_tonnage_alt_side?: Maybe;
  app_hvac_tonnage_alt_source?: Maybe;
}

/**
 * The Device List marker's `title`: the disagreement, then the value used and
 * why. With no value (the ductless case) it says why there is none. Null when
 * nothing disagrees, because then there is no marker.
 */
export function tonnageMarkerTitle(row: TonnageMarkerRow): string | null {
  if (row.app_hvac_tonnage_conflict !== true) return null;
  const value = row.app_hvac_tonnage;
  if (value === null || value === undefined || value === '') {
    return ductlessUnresolvedLabel(row.app_hvac_tonnage_alt);
  }
  const conflict = tonnageConflictLabel({
    alt: row.app_hvac_tonnage_alt,
    altSide: row.app_hvac_tonnage_alt_side,
    altSource: row.app_hvac_tonnage_alt_source,
  });
  const answer = tonnageAnswerLabel({
    component: row.app_hvac_tonnage_component,
    source: row.app_hvac_tonnage_source,
    capacitySource: row.app_hvac_tonnage_capacity_source,
    value,
  });
  return `${conflict} ${value} t is used: ${lowerFirst(answer)}.`;
}

/**
 * The coil record's own evidence, on the coil card. The same table as the
 * answer line without the "up to" row, which never applies to a coil.
 */
export function coilEvidenceLabel(f: { source: Maybe; capacitySource: Maybe }): string {
  return evidenceSentence('coil', f.source, f.capacitySource);
}

export { lowerFirst as lowerFirstLetter };
